/**
 * The colour arithmetic the Puida palette needs, and nothing else.
 *
 * WHY NOT chroma-js, which puida.com uses. chroma-js 3 ships as ESM only, and
 * this package is CommonJS for the same reason lasti's `@lasti/pricing` is: the
 * API is a CJS Nest build and imports it directly. What is needed here is HSV
 * to RGB, hex, CIE Lab (for `darken`) and WCAG luminance, so the dependency
 * would carry a whole colour library across a module boundary for about eighty
 * lines of arithmetic.
 *
 * The constants are chroma-js's own (D65 white point, sRGB matrix, `Kn = 18`),
 * so the outputs match puida.com exactly. `test/color.test.ts` holds palettes
 * produced by puida.com's chroma-js and fails if this ever drifts from them;
 * the brand is only consistent while that test passes.
 */

export type Rgb = readonly [r: number, g: number, b: number];

export function parseHex(hex: string): Rgb {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) throw new Error(`Not a #rrggbb colour: ${hex}`);
  const n = Number.parseInt(match[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Rounded and clipped, as chroma-js's `.hex()` does. */
export function toHex([r, g, b]: Rgb): string {
  const channel = (v: number) =>
    Math.min(255, Math.max(0, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** Hue in degrees, saturation and value in 0..1. */
export function toHsv([r, g, b]: Rgb): [h: number, s: number, v: number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const v = max / 255;
  const s = max === 0 ? 0 : delta / max;
  let h = 0;
  if (delta !== 0) {
    if (max === r) h = (g - b) / delta;
    else if (max === g) h = 2 + (b - r) / delta;
    else h = 4 + (r - g) / delta;
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s, v];
}

/**
 * chroma-js's `hsv2rgb`, operation for operation. The ORDER matters: chroma
 * scales `v` to 0..255 before multiplying, and doing it after instead put
 * hue 165's blue at 172.4999… rather than 172.5 — `ac` where puida.com says
 * `ad`. The palette test caught it; keep the arithmetic as it is.
 */
export function fromHsv(hue: number, s: number, value: number): Rgb {
  const v = value * 255;
  if (s === 0) return [v, v, v];
  let h = hue;
  if (h === 360) h = 0;
  if (h > 360) h -= 360;
  if (h < 0) h += 360;
  h /= 60;
  const i = Math.floor(h);
  const f = h - i;
  const p = v * (1 - s);
  const q = v * (1 - s * f);
  const t = v * (1 - s * (1 - f));
  const table: Rgb[] = [
    [v, t, p],
    [q, v, p],
    [p, v, t],
    [p, q, v],
    [t, p, v],
    [v, p, q],
  ];
  return table[i]!;
}

// --- CIE Lab, with chroma-js's constants ------------------------------------

const Kn = 18;
const Xn = 0.95047;
const Yn = 1;
const Zn = 1.08883;
const t0 = 0.137931034; // 4 / 29
const t1 = 0.206896552; // 6 / 29
const t2 = 0.12841855; // 3 * t1 * t1
const t3 = 0.008856452; // t1 * t1 * t1

const toLinear = (c: number) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};
const fromLinear = (c: number) =>
  255 * (c <= 0.00304 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
const xyzToLab = (t: number) => (t > t3 ? Math.cbrt(t) : t / t2 + t0);
const labToXyz = (t: number) => (t > t1 ? t * t * t : t2 * (t - t0));

export function toLab(rgb: Rgb): [l: number, a: number, b: number] {
  const [r, g, b] = rgb.map(toLinear) as [number, number, number];
  const x = xyzToLab((0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / Xn);
  const y = xyzToLab((0.2126729 * r + 0.7151522 * g + 0.072175 * b) / Yn);
  const z = xyzToLab((0.0193339 * r + 0.119192 * g + 0.9503041 * b) / Zn);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
}

export function fromLab([l, a, bb]: readonly [number, number, number]): Rgb {
  let y = (l + 16) / 116;
  let x = y + a / 500;
  let z = y - bb / 200;
  y = Yn * labToXyz(y);
  x = Xn * labToXyz(x);
  z = Zn * labToXyz(z);
  return [
    fromLinear(3.2404542 * x - 1.5371385 * y - 0.4985314 * z),
    fromLinear(-0.969266 * x + 1.8760108 * y + 0.041556 * z),
    fromLinear(0.0556434 * x - 0.2040259 * y + 1.0572252 * z),
  ];
}

/** chroma-js `.darken(amount)`: lower Lab lightness by 18 per step. */
export function darken(hex: string, amount = 1): string {
  const [l, a, b] = toLab(parseHex(hex));
  return toHex(fromLab([l - Kn * amount, a, b]));
}

// --- WCAG 2.x contrast -------------------------------------------------------

export function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map(toLinear) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** 1..21. Order of the arguments does not matter. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
